// ./src/components/features/Connectors/ConfigEditors/SlackConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
} from "@mantine/core";
import { IconInfoCircle, IconBrandSlack } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { SlackConnectorConfig } from "@/types";

export interface SlackConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: SlackConnectorConfig = {
  webhookUrl: "",
  showMetadata: true,
  showFiles: true,
  showViewButton: true,
  headerPattern: "📬 New submission: {{formName}}",
};

/**
 * Parse and validate Slack connector configuration
 */
const parseSlackConfig = (jsonString: string): SlackConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const SlackConnectorConfigEditor: React.FC<
  SlackConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<SlackConnectorConfig>(() =>
    parseSlackConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseSlackConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: SlackConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleWebhookUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, webhookUrl: e.target.value });
  };

  const handleHeaderPatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, headerPattern: e.target.value });
  };

  const handleShowMetadataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, showMetadata: e.currentTarget.checked });
  };

  const handleShowFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, showFiles: e.currentTarget.checked });
  };

  const handleShowViewButtonChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, showViewButton: e.currentTarget.checked });
  };

  // Validate webhook URL format
  const webhookUrlError = (() => {
    if (!config.webhookUrl) return undefined;
    if (!config.webhookUrl.startsWith("https://hooks.slack.com/")) {
      return "Must be a valid Slack webhook URL (starts with https://hooks.slack.com/)";
    }
    return undefined;
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconBrandSlack size={16} />} color="grape" variant="light">
        <Text size="sm">
          Create an Incoming Webhook in{" "}
          <Anchor
            href="https://api.slack.com/apps"
            target="_blank"
            rel="noopener noreferrer"
          >
            Slack API
          </Anchor>
          {" → Your Apps → Create App → Incoming Webhooks → Add New Webhook"}
        </Text>
      </Alert>

      {/* Webhook URL Field */}
      <TextInput
        label="Webhook URL"
        placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXX"
        description="Slack Incoming Webhook URL"
        value={config.webhookUrl}
        onChange={handleWebhookUrlChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
        error={webhookUrlError}
      />

      {/* Header Pattern Field */}
      <TextInput
        label="Header Pattern"
        placeholder="📬 New submission: {{formName}}"
        description="Customize the message header"
        value={config.headerPattern || "📬 New submission: {{formName}}"}
        onChange={handleHeaderPatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Message Options
        </Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Text size="sm">Show Metadata</Text>
              <Text size="xs" c="dimmed">
                Display submission ID and timestamp
              </Text>
            </div>
            <Switch
              checked={config.showMetadata !== false}
              onChange={handleShowMetadataChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Show Files</Text>
              <Text size="xs" c="dimmed">
                List attached files with sizes
              </Text>
            </div>
            <Switch
              checked={config.showFiles !== false}
              onChange={handleShowFilesChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Show View Button</Text>
              <Text size="xs" c="dimmed">
                Include "View Submission" button
              </Text>
            </div>
            <Switch
              checked={config.showViewButton !== false}
              onChange={handleShowViewButtonChange}
              disabled={disabled}
            />
          </Group>
        </Stack>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for header:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{formName}}"}</Code> — Form name
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{requestId}}"}</Code> — Submission ID
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* Preview Note */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Messages will include all visible form fields formatted as labeled
          values. Hidden fields are automatically excluded.
        </Text>
      </Alert>
    </Stack>
  );
};
